import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/lib/auth";
import { Icon } from "@/components/kit";
import DailyContent from "@/components/DailyContent";

const TONE = {
  danger: { c: "#F87171", bg: "rgba(239,68,68,.12)", b: "rgba(239,68,68,.28)" },
  warning: { c: "#FBBF24", bg: "rgba(245,158,11,.12)", b: "rgba(245,158,11,.28)" },
  success: { c: "#34D399", bg: "rgba(16,185,129,.12)", b: "rgba(16,185,129,.28)" },
  intelligence: { c: "#C084FC", bg: "rgba(168,85,247,.12)", b: "rgba(168,85,247,.28)" },
};
const PROMPTS = [
  "Draft a WhatsApp message for SIP investors…",
  "Who are my top 5 inactive HNI clients?",
  "Give me 3 conversation starters for today's market…",
  "Show clients who haven't invested in 6 months…",
  "Create today's investor content…",
];

function Ring({ value }) {
  const r = 52, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <svg width="128" height="128" viewBox="0 0 128 128" className="shrink-0">
      <circle cx="64" cy="64" r={r} fill="none" stroke="#222938" strokeWidth="10" />
      <circle cx="64" cy="64" r={r} fill="none" stroke="url(#ga)" strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 64 64)" style={{ transition: "stroke-dashoffset 1s ease" }} />
      <defs><linearGradient id="ga" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#FF8A00" /><stop offset="100%" stopColor="#F59E0B" /></linearGradient></defs>
      <text x="64" y="60" textAnchor="middle" className="font-head" fill="#F8FAFC" fontSize="30" fontWeight="800">{value}</text>
      <text x="64" y="80" textAnchor="middle" fill="#64748B" fontSize="11" fontWeight="600">/ 100</text>
    </svg>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [cmd, setCmd] = useState("");
  const [ph, setPh] = useState(0);
  const oppsRef = useRef(null);

  const load = useCallback(() => api.get("/dashboard/os").then((r) => setD(r.data)).catch((e) => console.error("OS load failed:", e)), []);
  const loadPulse = useCallback(() => api.get("/community/top-poll").then((r) => setPulse(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadPulse(); }, [load, loadPulse]);
  useEffect(() => { const t = setInterval(() => setPh((p) => (p + 1) % PROMPTS.length), 3200); return () => clearInterval(t); }, []);

  const ask = (text) => nav("/app/ai", { state: { prompt: text } });
  const submitCmd = (e) => { e.preventDefault(); if (cmd.trim()) ask(cmd.trim()); };
  const votePulse = async (i) => {
    try { const { data } = await api.post(`/community/posts/${pulse.id}/poll-vote`, { option: i }); setPulse((p) => ({ ...p, poll_results: data })); }
    catch (err) { console.error("pulse vote failed:", err); }
  };

  if (!d) return <div className="text-slate-500 font-head">Getting your business ready…</div>;
  const { greeting_name, today, opportunities, noticed, growth_score: gs, momentum, streak, tools } = d;

  return (
    <div className="fade-up space-y-6">
      {/* L1 — ATTENTION: What should I do today? */}
      <section className="rounded-[26px] bg-gradient-to-br from-[#12151F] to-[#0C0E14] border border-[#222938] p-6 md:p-8 relative overflow-hidden" data-testid="os-hero">
        <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-amber-500/10 blur-3xl" />
        <div className="relative flex flex-col lg:flex-row gap-8 items-start">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold tracking-[0.16em] uppercase text-amber-400 mb-2">What should I do today?</div>
            <h1 className="font-head text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-50">Good morning, {greeting_name}</h1>
            <p className="text-slate-400 mt-2 text-base">Your business needs attention in <b className="text-slate-200">{today.attention.length} areas</b> today.</p>
            <div className="flex flex-wrap gap-2 mt-4" data-testid="os-attention">
              {today.attention.map((a) => (
                <button key={a.label} onClick={() => nav(a.path)} data-testid={`attention-${a.label.replace(/\s/g, "-")}`}
                  className="flex items-center gap-2 bg-[#181D2A] border border-[#2E384D] hover:border-amber-500/40 rounded-full pl-2 pr-4 py-1.5 transition-colors">
                  <span className="w-7 h-7 rounded-full bg-amber-500/12 grid place-items-center"><Icon name={a.icon} size={14} className="text-amber-400" /></span>
                  <span className="font-head font-bold text-slate-100">{a.count}</span>
                  <span className="text-sm text-slate-400">{a.label}</span>
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-6">
              <button onClick={() => oppsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} data-testid="start-my-day-btn"
                className="grad-amber text-black font-semibold px-6 py-3 rounded-xl amber-glow hover:brightness-105 transition inline-flex items-center gap-2">
                <Icon name="Play" size={17} /> Start My Day
              </button>
              <button onClick={() => nav("/app/weekly")} data-testid="hero-weekly-brief-btn"
                className="bg-[#181D2A] border border-[#2E384D] hover:border-amber-500/40 text-slate-200 font-semibold px-5 py-3 rounded-xl transition-colors inline-flex items-center gap-2">
                <Icon name="CalendarCheck" size={16} /> This week's brief
              </button>
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <div className="w-32 h-2 rounded-full bg-[#222938] overflow-hidden"><div className="h-full grad-amber" style={{ width: `${today.day_pct}%` }} /></div>
                <span>Today {today.day_pct}% done · {today.done}/{today.total} tasks</span>
              </div>
            </div>
          </div>
          {/* Growth score */}
          <button onClick={() => document.getElementById("os-progress")?.scrollIntoView({ behavior: "smooth" })} data-testid="hero-growth-score"
            className="flex items-center gap-4 bg-[#0E1117] border border-[#222938] rounded-2xl p-4 pr-6 text-left hover:border-amber-500/30 transition-colors">
            <Ring value={gs.total} />
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Business Growth Score</div>
              <div className="font-head font-bold text-lg text-slate-100 mt-0.5">{gs.level}</div>
              <div className="text-xs text-amber-400 mt-1">{gs.to_next} pts to {gs.next_level}</div>
            </div>
          </button>
        </div>
      </section>

      {/* Command bar */}
      <form onSubmit={submitCmd} className="relative" data-testid="os-command-form">
        <div className="flex items-center gap-3 bg-[#141822] border border-[#2E384D] focus-within:border-amber-500/50 rounded-2xl px-4 py-3 transition-colors">
          <span className="w-9 h-9 rounded-xl grad-amber grid place-items-center text-black shrink-0"><Icon name="Sparkles" size={18} /></span>
          <input value={cmd} onChange={(e) => setCmd(e.target.value)} placeholder={PROMPTS[ph]} data-testid="os-command-input"
            className="flex-1 bg-transparent outline-none text-slate-100 text-sm md:text-base placeholder:text-slate-500" />
          <button type="submit" data-testid="os-command-submit" className="grad-amber text-black font-semibold px-4 py-2 rounded-xl text-sm hidden sm:inline-flex items-center gap-1">
            Get it done <Icon name="ArrowRight" size={15} />
          </button>
        </div>
      </form>

      {/* L2 — ACTION: Today's Opportunities */}
      <section ref={oppsRef} id="os-opps">
        <SectionHead icon="Target" color="#FF8A00" title="Today's Opportunities" sub={`${opportunities.length} ways to move your business forward right now`} />
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="os-opportunities">
          {opportunities.map((o) => {
            const t = TONE[o.priority_tone] || TONE.warning;
            return (
              <div key={o.id} className="card-mfx p-5 hover-lift flex flex-col" data-testid={`opp-${o.type}`}>
                <span className="self-start text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 mb-3" style={{ background: t.bg, color: t.c, border: `1px solid ${t.b}` }}>{o.priority}</span>
                <div className="font-head font-semibold text-slate-100 leading-snug">{o.title}</div>
                <div className="text-xs text-slate-500 mt-1">{o.context}</div>
                <p className="text-sm text-slate-400 mt-3 flex-1 leading-relaxed">{o.reason}</p>
                <button onClick={() => nav(o.path)} data-testid={`opp-cta-${o.type}`}
                  className="mt-4 inline-flex items-center justify-center gap-1.5 bg-[#1E2435] hover:bg-[#252C40] border border-[#2E384D] text-slate-100 font-semibold text-sm rounded-xl px-3 py-2.5 transition-colors">
                  {o.cta} <Icon name="ArrowRight" size={15} />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* L3 — INTELLIGENCE: MFDx noticed */}
      <section>
        <SectionHead icon="Sparkles" color="#C084FC" title="MFDx noticed…" sub="Your Copilot is watching your business so you don't have to" />
        <div className="card-mfx p-2" data-testid="os-noticed">
          {noticed.map((n, i) => {
            const t = TONE[n.tone] || TONE.intelligence;
            return (
              <button key={n.id} onClick={() => nav(n.path)} data-testid={`noticed-${n.id}`}
                className={`w-full text-left flex items-center gap-3 p-3.5 rounded-xl hover:bg-[#1E2435] transition-colors ${i < noticed.length - 1 ? "border-b border-[#222938]" : ""}`}>
                <span className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: t.bg }}><Icon name={n.icon} size={17} style={{ color: t.c }} /></span>
                <span className="text-sm text-slate-300 flex-1">{n.text}</span>
                <Icon name="ChevronRight" size={16} className="text-slate-600 shrink-0" />
              </button>
            );
          })}
          <button onClick={() => ask("What's the most important thing I should focus on in my business today?")} data-testid="noticed-ask-ai"
            className="w-full mt-1 flex items-center justify-center gap-2 text-sm font-semibold text-amber-400 hover:bg-amber-500/8 rounded-xl py-2.5 transition-colors">
            <Icon name="MessageSquare" size={15} /> Ask Copilot what to do about these
          </button>
        </div>
      </section>

      {/* L4 — PROGRESS */}
      <section id="os-progress">
        <SectionHead icon="Gauge" color="#10B981" title="How you're doing" sub={`You're a ${gs.level} — ${gs.to_next} points from ${gs.next_level}`} />
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="card-mfx p-6 lg:col-span-2" data-testid="os-growth-pillars">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-head font-semibold text-slate-100">Growth Score breakdown</h3>
              <span className="font-head font-bold text-2xl text-amber-400">{gs.total}<span className="text-slate-600 text-base">/100</span></span>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {gs.pillars.map((p) => (
                <div key={p.key} data-testid={`pillar-${p.key}`}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-400">{p.label}</span><span className="font-semibold text-slate-200 tabular-nums">{p.score}</span></div>
                  <div className="h-2 rounded-full bg-[#222938] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p.score}%`, background: p.color, transition: "width 1s ease" }} /></div>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="card-mfx p-5" data-testid="os-momentum">
              <div className="flex items-center gap-2 text-emerald-400"><Icon name="TrendingUp" size={18} /><span className="font-head font-semibold text-slate-100">Business Momentum</span></div>
              <div className="font-head font-extrabold text-3xl text-emerald-400 mt-2">+{momentum.pct}%</div>
              <p className="text-sm text-slate-400 mt-1">{momentum.label}</p>
            </div>
            <div className="card-mfx p-5" data-testid="os-streak">
              <div className="flex items-center gap-2"><Icon name="Flame" size={18} className="text-amber-400" /><span className="font-head font-semibold text-slate-100">Activity Streak</span></div>
              <div className="font-head font-extrabold text-3xl text-slate-50 mt-2">{streak.days} days</div>
              <p className="text-sm text-slate-400 mt-1">{streak.label}</p>
            </div>
          </div>
        </div>
      </section>

      {/* L5 — DISCOVERY */}
      <section>
        <SectionHead icon="Compass" color="#06B6D4" title="What else MFDx can do for you" sub="Pick a business outcome — MFDx handles the how" />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="os-tools">
          {tools.map((tl) => (
            <button key={tl.key} onClick={() => nav(tl.path)} data-testid={`tool-${tl.key}`}
              className="card-mfx p-4 hover-lift text-left">
              <span className="w-10 h-10 rounded-xl grid place-items-center mb-3" style={{ background: `${tl.color}1f` }}><Icon name={tl.icon} size={20} style={{ color: tl.color }} /></span>
              <div className="font-head font-semibold text-sm text-slate-100">{tl.label}</div>
              <div className="text-xs text-slate-500 mt-0.5 leading-snug">{tl.desc}</div>
            </button>
          ))}
        </div>

        <DailyContent />

        {pulse && pulse.poll_results && (
          <div className="card-mfx p-5 mt-4" data-testid="community-pulse">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Activity" size={18} className="text-emerald-400" />
              <h3 className="font-head font-semibold text-slate-100">Community pulse</h3>
              <span className="text-[10px] font-bold bg-emerald-500/12 text-emerald-400 rounded-full px-2 py-0.5 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot" /> LIVE</span>
              <button onClick={() => nav("/app/community")} data-testid="pulse-see-all" className="ml-auto text-sm font-semibold text-amber-400 flex items-center gap-1">See all polls <Icon name="ChevronRight" size={14} /></button>
            </div>
            <div className="font-head font-semibold text-slate-200 mb-3 flex items-start gap-2"><Icon name="BarChart3" size={16} className="text-amber-400 mt-0.5 shrink-0" />{pulse.title}</div>
            <div className="space-y-1.5">
              {pulse.poll_results.options.map((o, i) => {
                const chosen = pulse.poll_results.my_choice === i;
                return (
                  <button key={o.text} onClick={() => votePulse(i)} data-testid={`pulse-option-${i}`}
                    className={`relative w-full text-left rounded-lg border px-3 py-2 overflow-hidden transition-colors ${chosen ? "border-amber-500/50" : "border-[#2E384D] hover:border-amber-500/30"}`}>
                    <div className="absolute inset-y-0 left-0 rounded-lg" style={{ width: `${o.pct}%`, background: chosen ? "rgba(255,138,0,.18)" : "rgba(255,255,255,.05)" }} />
                    <div className="relative flex items-center justify-between text-sm text-slate-200">
                      <span className="flex items-center gap-1.5">{chosen && <Icon name="Check" size={13} className="text-amber-400" />}{o.text}</span>
                      <span className="text-slate-400 font-medium tabular-nums">{o.pct}% · {o.votes}</span>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="text-[11px] text-slate-500 pt-2">{pulse.poll_results.total} votes · {pulse.poll_results.my_choice != null ? "you voted · tap to change" : "tap an option to vote"}</div>
          </div>
        )}
      </section>
    </div>
  );
}

function SectionHead({ icon, color, title, sub }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <div className="w-9 h-9 rounded-xl grid place-items-center shrink-0" style={{ background: `${color}1f` }}><Icon name={icon} size={18} style={{ color }} /></div>
      <div><h2 className="font-head font-semibold text-lg text-slate-100 leading-tight">{title}</h2><p className="text-xs text-slate-500">{sub}</p></div>
    </div>
  );
}
